import React from "react";

import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";

import {

  Building2,

  Eye,

  EyeOff,

  KeyRound,

  ShieldCheck,

  UserPlus,

} from "lucide-react";

import { useAuth0 } from "@auth0/auth0-react";

import "./RegisterOssStep1Page.css";

import TermsConditionsModal from "../../legal/TermsConditionsModal";

import PostalCodeCoverageSelector from "../../../../components/maps/PostalCodeCoverageSelector";

import PasswordStrengthChecklist, {

  isStrongPassword,

} from "../../../../components/PasswordStrengthChecklist";

import {

  PROPERTY_PURCHASE_COUNTRIES,

  getPhoneCodeByCountry,

} from "../../../../data/countries";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const OSS_AUTH_CONNECTION = "LEGUP-OSS";

const REGISTRATION_STORAGE_KEY = "legup_oss_join_request_step1";

/* Section */

const REGISTRATION_MODES = {

  NEW_OSS: "NEW_OSS",

  JOIN_EXISTING: "JOIN_EXISTING",

};

/* Section */

const DEFAULT_FORM = {

  first_name: "",

  last_name: "",

  role_in_company: "",

  company_name: "",

  country_code: "",

  years_of_experience: "",

  website_url: "",

  market_region_id: "",

  email: "",
  confirm_email: "",

  phone: "",

  is_advisory: false,

  is_turnkey_renovation: false,

  coverage_postal_codes: [],

  message: "",

/* Section */

  company_key: "",

  password: "",

  confirm_password: "",

  agree_terms: false,

};

/* Section */

function isValidEmail(email = "") {

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

}

function validatePhone(phone = "", t) {

  const clean = String(phone || "").replace(/\D/g, "");

  if (!clean) {

    return t("validation.phoneRequired");

  }

  if (clean.length < 6) {

    return t("validation.phoneTooShort", "Phone number is too short.");

  }

  if (clean.length > 10) {

    return t("validation.phoneTooLong", "Phone number is too long.");

  }

  return "";

}

/* Section */

function getFirstApiError(data, fallback) {

  if (!data) {

    return fallback;

  }

  if (typeof data.detail === "string") {

    return data.detail;

  }

  if (typeof data.message === "string") {

    return data.message;

  }

  const preferredKeys = [

    "company_key",

    "email",
    "confirm_email",
    "role_in_company",

    "password",

    "first_name",

    "last_name",

    "company_name",

    "country_code",

    "years_of_experience",

    "non_field_errors",

  ];

  for (const key of preferredKeys) {

    const value = data[key];

    if (Array.isArray(value) && value.length) {

      return String(value[0]);

    }

    if (typeof value === "string") {

      return value;

    }

  }

  return fallback;

}

/* Section */

export default function RegisterOssStep1Page() {

  const { t } = useTranslation("oss-registration-step1");

  const { loginWithRedirect } = useAuth0();

  const didMountRef = React.useRef(false);

  const loginRedirectTimerRef = React.useRef(null);

  const [registrationMode, setRegistrationMode] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(REGISTRATION_STORAGE_KEY) || "{}");
      return saved.registrationMode === REGISTRATION_MODES.JOIN_EXISTING
        ? REGISTRATION_MODES.JOIN_EXISTING
        : REGISTRATION_MODES.NEW_OSS;
    } catch {
      return REGISTRATION_MODES.NEW_OSS;
    }
  });

  const [showPassword, setShowPassword] = React.useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const [form, setForm] = React.useState(() => {

    try {

      const saved = localStorage.getItem(REGISTRATION_STORAGE_KEY);

      if (!saved) {

        return DEFAULT_FORM;

      }

      const parsed = JSON.parse(saved);

      return {

        ...DEFAULT_FORM,

        ...parsed.form,

      };

    } catch {

      return DEFAULT_FORM;

    }

  });

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [marketRegions, setMarketRegions] = React.useState([]);

  const [loadingMarketRegions, setLoadingMarketRegions] = React.useState(false);

  const [loading, setLoading] = React.useState(false);

  const [err, setErr] = React.useState("");

  const [ok, setOk] = React.useState("");

  const [termsModalOpen, setTermsModalOpen] = React.useState(false);

/* Section */

  React.useEffect(() => {

    return () => {

      if (loginRedirectTimerRef.current) {

        window.clearTimeout(loginRedirectTimerRef.current);

      }

    };

  }, []);

  React.useEffect(() => {

    if (registrationMode !== REGISTRATION_MODES.NEW_OSS || !form.country_code) {

      setMarketRegions([]);

      return;

    }

    let cancelled = false;

    async function loadMarketRegions() {

      try {

        setLoadingMarketRegions(true);

        const params = new URLSearchParams({

          country_code: form.country_code,

          require_lender: "false",

        });

        const response = await fetch(

          `${API_BASE}/api/market-regions/?${params.toString()}`,

        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {

          throw new Error(

            data?.detail || data?.message || "Could not load market regions.",

          );

        }

        if (cancelled) {

          return;

        }

        const regions = Array.isArray(data?.results) ? data.results : [];

        setMarketRegions(regions);

/* Section */

        if (regions.length === 1) {

          setForm((previous) => ({

            ...previous,

            market_region_id: String(regions[0].id),

          }));

        }

      } catch (error) {

        if (!cancelled) {

          console.error("Could not load OSS market regions:", error);

          setMarketRegions([]);

        }

      } finally {

        if (!cancelled) {

          setLoadingMarketRegions(false);

        }

      }

    }

    loadMarketRegions();

    return () => {

      cancelled = true;

    };

  }, [form.country_code, registrationMode]);

/* Section */

  React.useEffect(() => {

    if (!didMountRef.current) {

      didMountRef.current = true;

      return;

    }

    try {

      localStorage.setItem(

        REGISTRATION_STORAGE_KEY,

        JSON.stringify({

          registrationMode,

          form: {

            ...form,

/* Section */

            password: "",

            confirm_password: "",

          },

        }),

      );

    } catch (error) {

      console.error("Failed to save OSS registration form:", error);

    }

  }, [form, registrationMode]);

/* Section */

  function changeRegistrationMode(nextMode) {

    if (nextMode === registrationMode) {

      return;

    }

    setRegistrationMode(nextMode);

    setFieldErrors({});

    setErr("");

    setOk("");

    setShowPassword(false);

    setShowConfirmPassword(false);

  }

/* Section */

  function onChange(e) {

    const { name, value, type, checked } = e.target;

    let nextValue = type === "checkbox" ? checked : value;

/* Section */

    if (name === "first_name" || name === "last_name") {

      nextValue = value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, "");

    }

/* Section */

    if (name === "phone") {

      nextValue = value.replace(/\D/g, "").slice(0, 10);

    }

    setForm((previous) => ({

      ...previous,

      [name]: nextValue,

      ...(name === "country_code" && value !== previous.country_code

        ? {

            phone: "",

            market_region_id: "",

            coverage_postal_codes: [],

          }

        : {}),

    }));

    setFieldErrors((previous) => ({

      ...previous,

      [name]: "",

    }));

    if (err) {

      setErr("");

    }

    if (ok) {

      setOk("");

    }

  }

/* Section */

  function validateConfirmEmail(errors) {
    const email = form.email.trim().toLowerCase();
    const confirmation = form.confirm_email.trim().toLowerCase();

    if (!confirmation) {
      errors.confirm_email = t("validation.confirmEmailRequired", "Confirm your email address.");
    } else if (!isValidEmail(confirmation)) {
      errors.confirm_email = t("validation.emailInvalid", "Enter a valid email address.");
    } else if (email && email !== confirmation) {
      errors.confirm_email = t("validation.emailsDoNotMatch", "Email addresses do not match.");
    }
  }

  function validateNewOss() {

    const errors = {};

    const nameRegex = /^[A-Za-zÀ-ÿ\s'-]+$/;

    if (!form.first_name.trim()) {

      errors.first_name = t("validation.firstNameRequired");

    } else if (!nameRegex.test(form.first_name.trim())) {

      errors.first_name = t("validation.lettersOnly");

    }

    if (!form.last_name.trim()) {

      errors.last_name = t("validation.lastNameRequired");

    } else if (!nameRegex.test(form.last_name.trim())) {

      errors.last_name = t("validation.lettersOnly");

    }

    if (!form.role_in_company.trim()) {

      errors.role_in_company = t("validation.roleRequired");

    }

    if (!form.company_name.trim()) {

      errors.company_name = t("validation.companyNameRequired");

    }

    if (!form.country_code) {

      errors.country_code = t("validation.countryRequired");

    }

    if (!form.market_region_id) {

      errors.market_region_id = t(

        "validation.marketRegionRequired",

        "Select a market region.",

      );

    }

    if (!form.email.trim()) {

      errors.email = t("validation.emailRequired");

    } else if (!isValidEmail(form.email)) {

      errors.email = t(

        "validation.emailInvalid",

        "Enter a valid email address.",

      );

    }

    const phoneError = validatePhone(form.phone, t);

    if (phoneError) {

      errors.phone = phoneError;

    }

    if (form.years_of_experience === "") {

      errors.years_of_experience = t("validation.experienceRequired");

    } else if (Number(form.years_of_experience) < 0) {

      errors.years_of_experience = t("validation.experienceNegative");

    }

    if (!form.is_advisory && !form.is_turnkey_renovation) {

      errors.oss_type = t("validation.ossTypeRequired");

    }

    if (

      !Array.isArray(form.coverage_postal_codes) ||

      form.coverage_postal_codes.length === 0

    ) {

      errors.coverage_postal_codes = t(

        "validation.regionsCoveredRequired",

        "Select at least one postal code.",

      );

    }

    if (!form.agree_terms) {

      errors.agree_terms = t(

        "validation.agreeTermsRequired",

        "You must accept the Terms and Conditions.",

      );

    }

    validateConfirmEmail(errors);

    return errors;

  }

/* Section */

  function validateExistingOss() {

    const errors = {};

    const nameRegex = /^[A-Za-zÀ-ÿ\s'-]+$/;

    if (!form.first_name.trim()) {

      errors.first_name = t("validation.firstNameRequired");

    } else if (!nameRegex.test(form.first_name.trim())) {

      errors.first_name = t("validation.lettersOnly");

    }

    if (!form.last_name.trim()) {

      errors.last_name = t("validation.lastNameRequired");

    } else if (!nameRegex.test(form.last_name.trim())) {

      errors.last_name = t("validation.lettersOnly");

    }

    if (!form.email.trim()) {

      errors.email = t("validation.emailRequired");

    } else if (!isValidEmail(form.email)) {

      errors.email = t(

        "validation.emailInvalid",

        "Enter a valid email address.",

      );

    }

    if (!form.role_in_company.trim()) {
      errors.role_in_company = t("validation.roleRequired", "Enter your role in the organisation.");
    }

    if (!form.company_key.trim()) {

      errors.company_key = t(

        "validation.companyKeyRequired",

        "Company key is required.",

      );

    }

    if (!form.password) {

      errors.password = t(

        "validation.passwordRequired",

        "Password is required.",

      );

    } else if (!isStrongPassword(form.password)) {

      errors.password = t(

        "validation.passwordWeak",

        "Password does not meet the security requirements.",

      );

    }

    if (!form.confirm_password) {

      errors.confirm_password = t(

        "validation.confirmPasswordRequired",

        "Confirm your password.",

      );

    } else if (form.password !== form.confirm_password) {

      errors.confirm_password = t(

        "validation.passwordMismatch",

        "Passwords do not match.",

      );

    }

    if (!form.agree_terms) {

      errors.agree_terms = t(

        "validation.agreeTermsRequired",

        "You must accept the Terms and Conditions.",

      );

    }

    validateConfirmEmail(errors);

    return errors;

  }

/* Section */

  async function submitNewOss() {

    const phoneCode = getPhoneCodeByCountry(form.country_code);

    const payload = {

      first_name: form.first_name.trim(),

      last_name: form.last_name.trim(),

      role_in_company: form.role_in_company.trim(),

      company_name: form.company_name.trim(),

      country_code: form.country_code,

      market_region_id: Number(form.market_region_id),

      years_of_experience: Number(form.years_of_experience),

      website_url: form.website_url.trim(),

      message: form.message.trim(),

      email: form.email.trim().toLowerCase(),

      phone: phoneCode && form.phone ? `${phoneCode} ${form.phone}` : "",

      is_advisory: form.is_advisory,

      is_turnkey_renovation: form.is_turnkey_renovation,

      coverage_postal_codes: form.coverage_postal_codes.map((item) => ({

        country_code: item.country_code,

        postal_code: item.postal_code,

        place_id: item.place_id,

      })),

    };

    const response = await fetch(`${API_BASE}/api/oss/join-request/`, {

      method: "POST",

      headers: {

        "Content-Type": "application/json",

      },

      body: JSON.stringify(payload),

    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {

      throw new Error(getFirstApiError(data, t("messages.requestFailed")));

    }

    return data;

  }

/* Section */

  async function submitExistingOss() {

    const payload = {

      first_name: form.first_name.trim(),

      last_name: form.last_name.trim(),

      email: form.email.trim().toLowerCase(),

      password: form.password,

      company_key: form.company_key.trim(),
      role_in_company: form.role_in_company.trim(),

    };

    const response = await fetch(

      `${API_BASE}/api/oss/register-with-company-key/`,

      {

        method: "POST",

        headers: {

          "Content-Type": "application/json",

        },

        body: JSON.stringify(payload),

      },

    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {

      const apiError = getFirstApiError(

        data,

        t(

          "messages.employeeRegistrationFailed",

          "Could not create your OSS employee account.",

        ),

      );

/* Section */

      setFieldErrors((previous) => ({

        ...previous,

        ...(data.company_key

          ? {

              company_key: Array.isArray(data.company_key)

                ? data.company_key[0]

                : data.company_key,

            }

          : {}),

        ...(data.email

          ? {

              email: Array.isArray(data.email) ? data.email[0] : data.email,

            }

          : {}),

        ...(data.password

          ? {

              password: Array.isArray(data.password)

                ? data.password[0]

                : data.password,

            }

          : {}),

      }));

      throw new Error(apiError);

    }

    return data;

  }

/* Section */

  function goToOssLoginAfterRegistration(email) {

    sessionStorage.setItem(AUTH_CONNECTION_KEY, OSS_AUTH_CONNECTION);

    loginWithRedirect({

      appState: {

        returnTo: "/post-login",

      },

      authorizationParams: {

        screen_hint: "login",

        prompt: "login",

        connection: OSS_AUTH_CONNECTION,

        ...(email

          ? {

              login_hint: email,

            }

          : {}),

      },

    });

  }

/* Section */

  async function submit(e) {

    e.preventDefault();

    setErr("");

    setOk("");

    const errors =

      registrationMode === REGISTRATION_MODES.NEW_OSS

        ? validateNewOss()

        : validateExistingOss();

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {

      setErr(t("messages.correctHighlighted"));

      return;

    }

    setLoading(true);

    try {

      if (registrationMode === REGISTRATION_MODES.NEW_OSS) {

        await submitNewOss();

        localStorage.removeItem(REGISTRATION_STORAGE_KEY);

        setFieldErrors({});

        setOk(t("messages.requestSubmitted"));

        setForm(DEFAULT_FORM);

      } else {

        const data = await submitExistingOss();

        const registeredEmail = form.email.trim().toLowerCase();

        localStorage.removeItem(REGISTRATION_STORAGE_KEY);

        setFieldErrors({});

        setOk(

          t(

            "messages.employeeRegistered",

            "Your account has been created and linked to {{organisation}}. Verify your email and sign in to continue.",

            {

              organisation:

                data.organisation_name ||

                t("messages.yourOrganisation", "your One-Stop Shop"),

            },

          ),

        );

        setForm(DEFAULT_FORM);

        loginRedirectTimerRef.current = window.setTimeout(() => {

          goToOssLoginAfterRegistration(registeredEmail);

        }, 2000);

      }

    } catch (ex) {

      setErr(ex?.message || String(ex));

    } finally {

      setLoading(false);

    }

  }

  const isNewOss = registrationMode === REGISTRATION_MODES.NEW_OSS;

  const isJoinExisting = registrationMode === REGISTRATION_MODES.JOIN_EXISTING;

/* Section */

  return (

    <div className="oss-step1-page">

      {/* Section */}

      <section className="oss-step1-hero">

        <p className="oss-step1-kicker">{t("step1.kicker")}</p>

        <h1>{t("step1.title")}</h1>

        <p>{t("step1.subtitle")}</p>

      </section>

      {/* Section */}

      <section className="oss-registration-mode">

        <div className="oss-registration-mode__heading">

          <span>{t("registrationMode.eyebrow", "Choose how to continue")}</span>

          <h2>{t("registrationMode.title", "Register your One-Stop Shop")}</h2>

          <p>

            {t(

              "registrationMode.description",

              "Create a new One-Stop Shop request or join an organisation that is already registered on LEG-UP.",

            )}

          </p>

        </div>

        <div className="oss-registration-mode__options">

          <button

            type="button"

            className={`oss-registration-mode__option ${

              isNewOss ? "oss-registration-mode__option--active" : ""

            }`}

            onClick={() => changeRegistrationMode(REGISTRATION_MODES.NEW_OSS)}

          >

            <span className="oss-registration-mode__icon">

              <Building2 size={22} />

            </span>

            <span className="oss-registration-mode__copy">

              <strong>

                {t("registrationMode.new.title", "Register a new OSS")}

              </strong>

              <small>

                {t(

                  "registrationMode.new.description",

                  "Submit your organisation for platform approval and complete the OSS onboarding process.",

                )}

              </small>

            </span>

            {isNewOss ? (

              <ShieldCheck className="oss-registration-mode__check" size={19} />

            ) : null}

          </button>

          <button

            type="button"

            className={`oss-registration-mode__option ${

              isJoinExisting ? "oss-registration-mode__option--active" : ""

            }`}

            onClick={() =>

              changeRegistrationMode(REGISTRATION_MODES.JOIN_EXISTING)

            }

          >

            <span className="oss-registration-mode__icon">

              <UserPlus size={22} />

            </span>

            <span className="oss-registration-mode__copy">

              <strong>

                {t("registrationMode.existing.title", "Join an existing OSS")}

              </strong>

              <small>

                {t(

                  "registrationMode.existing.description",

                  "Create your own employee account using the company key provided by your OSS administrator.",

                )}

              </small>

            </span>

            {isJoinExisting ? (

              <ShieldCheck className="oss-registration-mode__check" size={19} />

            ) : null}

          </button>

        </div>

      </section>

      {/* Section */}

      {isNewOss ? (

        <div className="oss-step1-note">{t("step1.note")}</div>

      ) : (

        <div className="oss-step1-note oss-step1-note--company-key">

          <KeyRound size={18} />

          <span>

            {t(

              "registrationMode.existing.note",

              "Each employee receives a separate LEG-UP account and uses their own MFA. The company key only links the new account to the existing OSS organisation.",

            )}

          </span>

        </div>

      )}

      {/* Section */}

      <form className="oss-step1-card" onSubmit={submit}>

        <div className="oss-step1-card-header">

          <div>

            <h2>

              {isNewOss

                ? t("step1.sectionTitle")

                : t("employee.sectionTitle", "Employee account")}

            </h2>

            <p>

              {isNewOss

                ? t("step1.sectionSubtitle")

                : t(

                    "employee.sectionSubtitle",

                    "Enter your personal account details and the company key for your registered One-Stop Shop.",

                  )}

            </p>

          </div>

          {isJoinExisting ? (

            <span className="oss-step1-employee-badge">

              <KeyRound size={14} />

              {t("employee.companyKeyBadge", "Company key")}

            </span>

          ) : null}

        </div>

        {/* Section */}

        <div className="oss-step1-grid">

          <div className="oss-step1-field">

            <label>

              {t("fields.firstName")}

              <span className="oss-required">*</span>

            </label>

            <input

              name="first_name"

              placeholder={t("placeholders.firstName")}

              value={form.first_name}

              onChange={onChange}

              className={fieldErrors.first_name ? "oss-step1-input-error" : ""}

            />

            {fieldErrors.first_name ? (

              <span>{fieldErrors.first_name}</span>

            ) : null}

          </div>

          <div className="oss-step1-field">

            <label>

              {t("fields.lastName")}

              <span className="oss-required">*</span>

            </label>

            <input

              name="last_name"

              placeholder={t("placeholders.lastName")}

              value={form.last_name}

              onChange={onChange}

              className={fieldErrors.last_name ? "oss-step1-input-error" : ""}

            />

            {fieldErrors.last_name ? (

              <span>{fieldErrors.last_name}</span>

            ) : null}

          </div>

          {isJoinExisting ? (
            <div className="oss-step1-field">
              <label>
                {t("fields.roleInCompany", "Role in organisation")}
                <span className="oss-required">*</span>
              </label>
              <input
                name="role_in_company"
                placeholder={t("placeholders.roleInCompany", "e.g. Renovation adviser")}
                value={form.role_in_company}
                onChange={onChange}
                className={fieldErrors.role_in_company ? "oss-step1-input-error" : ""}
              />
              {fieldErrors.role_in_company ? <span>{fieldErrors.role_in_company}</span> : null}
            </div>
          ) : null}

          {/* Section */}

          {isNewOss ? (

            <>

              <div className="oss-step1-field">

                <label>

                  {t("fields.companyName")}

                  <span className="oss-required">*</span>

                </label>

                <input

                  name="company_name"

                  placeholder={t("placeholders.companyName")}

                  value={form.company_name}

                  onChange={onChange}

                  className={

                    fieldErrors.company_name ? "oss-step1-input-error" : ""

                  }

                />

                {fieldErrors.company_name ? (

                  <span>{fieldErrors.company_name}</span>

                ) : null}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("fields.roleInCompany")}

                  <span className="oss-required">*</span>

                </label>

                <input

                  name="role_in_company"

                  placeholder={t("placeholders.roleInCompany")}

                  value={form.role_in_company}

                  onChange={onChange}

                  className={

                    fieldErrors.role_in_company ? "oss-step1-input-error" : ""

                  }

                />

                {fieldErrors.role_in_company ? (

                  <span>{fieldErrors.role_in_company}</span>

                ) : null}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("fields.country")}

                  <span className="oss-required">*</span>

                </label>

                <select

                  name="country_code"

                  value={form.country_code}

                  onChange={onChange}

                  className={

                    fieldErrors.country_code ? "oss-step1-input-error" : ""

                  }

                >

                  <option value="">{t("placeholders.country")}</option>

                  {PROPERTY_PURCHASE_COUNTRIES.map((country) => (

                    <option key={country.code} value={country.code}>

                      {country.label}

                    </option>

                  ))}

                </select>

                {fieldErrors.country_code ? (

                  <span>{fieldErrors.country_code}</span>

                ) : null}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("fields.marketRegion", "Market region")}

                  <span className="oss-required">*</span>

                </label>

                <select

                  name="market_region_id"

                  value={form.market_region_id}

                  onChange={onChange}

                  disabled={!form.country_code || loadingMarketRegions}

                  className={

                    fieldErrors.market_region_id ? "oss-step1-input-error" : ""

                  }

                >

                  <option value="">

                    {!form.country_code

                      ? t(

                          "placeholders.selectCountryFirst",

                          "Select a country first",

                        )

                      : loadingMarketRegions

                        ? t(

                            "placeholders.loadingMarketRegions",

                            "Loading market regions...",

                          )

                        : marketRegions.length === 0

                          ? t(

                              "placeholders.noMarketRegions",

                              "No active market regions available",

                            )

                          : t(

                              "placeholders.marketRegion",

                              "Select market region",

                            )}

                  </option>

                  {marketRegions.map((region) => (

                    <option key={region.id} value={region.id}>

                      {region.name}

                    </option>

                  ))}

                </select>

                {fieldErrors.market_region_id ? (

                  <span>{fieldErrors.market_region_id}</span>

                ) : null}

                {form.country_code &&

                !loadingMarketRegions &&

                marketRegions.length === 0 ? (

                  <small className="oss-step1-field-hint">

                    {t(

                      "fields.noMarketRegionHelp",

                      "There is currently no active LEG-UP market available for the selected country.",

                    )}

                  </small>

                ) : null}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("fields.yearsOfExperience")}

                  <span className="oss-required">*</span>

                </label>

                <input

                  name="years_of_experience"

                  type="number"

                  min="0"

                  placeholder={t("placeholders.yearsOfExperience")}

                  value={form.years_of_experience}

                  onChange={onChange}

                  className={

                    fieldErrors.years_of_experience

                      ? "oss-step1-input-error"

                      : ""

                  }

                />

                {fieldErrors.years_of_experience ? (

                  <span>{fieldErrors.years_of_experience}</span>

                ) : null}

              </div>

            </>

          ) : null}

          {/* Section */}

          <div className="oss-step1-field">

            <label>

              {t("fields.email")}

              <span className="oss-required">*</span>

            </label>

            <input

              name="email"

              type="email"

              placeholder={t("placeholders.email")}

              value={form.email}

              onChange={onChange}

              className={fieldErrors.email ? "oss-step1-input-error" : ""}

            />

            {fieldErrors.email ? <span>{fieldErrors.email}</span> : null}

          </div>

          <div className="oss-step1-field">
            <label>
              {t("fields.confirmEmail", "Confirm email")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="confirm_email"
              type="email"
              autoComplete="email"
              placeholder={t("placeholders.confirmEmail", "Repeat your email address")}
              value={form.confirm_email}
              onChange={onChange}
              className={fieldErrors.confirm_email ? "oss-step1-input-error" : ""}
            />
            {fieldErrors.confirm_email ? <span>{fieldErrors.confirm_email}</span> : null}
          </div>

          {/* Section */}

          {isNewOss ? (

            <>

              <div className="oss-step1-field">

                <label>

                  {t("fields.phone")}

                  <span className="oss-required">*</span>

                </label>

                <div

                  className={`oss-step1-phone-wrap ${

                    fieldErrors.phone ? "oss-step1-phone-wrap--error" : ""

                  }`}

                >

                  <span className="oss-step1-phone-prefix">

                    {getPhoneCodeByCountry(form.country_code) || "—"}

                  </span>

                  <input

                    name="phone"

                    inputMode="numeric"

                    placeholder={t("placeholders.phone")}

                    value={form.phone}

                    onChange={onChange}

                  />

                </div>

                {fieldErrors.phone ? <span>{fieldErrors.phone}</span> : null}

              </div>

              <div className="oss-step1-field">

                <label>{t("fields.website")}</label>

                <input

                  name="website_url"

                  type="url"

                  placeholder={t("placeholders.website")}

                  value={form.website_url}

                  onChange={onChange}

                />

              </div>

            </>

          ) : null}

          {/* Section */}

          {isJoinExisting ? (

            <>

              <div className="oss-step1-field oss-step1-field--full">

                <label>

                  {t("employee.companyKey", "Company key")}

                  <span className="oss-required">*</span>

                </label>

                <div className="oss-step1-key-input">

                  <KeyRound size={18} />

                  <input

                    name="company_key"

                    type="text"

                    autoComplete="off"

                    placeholder={t(

                      "employee.companyKeyPlaceholder",

                      "Enter the key provided by your OSS administrator",

                    )}

                    value={form.company_key}

                    onChange={onChange}

                    className={

                      fieldErrors.company_key ? "oss-step1-input-error" : ""

                    }

                  />

                </div>

                {fieldErrors.company_key ? (

                  <span>{fieldErrors.company_key}</span>

                ) : (

                  <small className="oss-step1-field-hint">

                    {t(

                      "employee.companyKeyHint",

                      "The company key identifies your existing OSS organisation. It is not your login password.",

                    )}

                  </small>

                )}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("employee.password", "Password")}

                  <span className="oss-required">*</span>

                </label>

                <div className="oss-step1-password-wrap">

                  <input

                    name="password"

                    type={showPassword ? "text" : "password"}

                    autoComplete="new-password"

                    placeholder={t(

                      "employee.passwordPlaceholder",

                      "Create your password",

                    )}

                    value={form.password}

                    onChange={onChange}

                    className={

                      fieldErrors.password ? "oss-step1-input-error" : ""

                    }

                  />

                  <button

                    type="button"

                    className="oss-step1-password-toggle"

                    onClick={() => setShowPassword((previous) => !previous)}

                    aria-label={

                      showPassword ? "Hide password" : "Show password"

                    }

                  >

                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}

                  </button>

                </div>

                {fieldErrors.password ? (

                  <span>{fieldErrors.password}</span>

                ) : null}

              </div>

              <div className="oss-step1-field">

                <label>

                  {t("employee.confirmPassword", "Confirm password")}

                  <span className="oss-required">*</span>

                </label>

                <div className="oss-step1-password-wrap">

                  <input

                    name="confirm_password"

                    type={showConfirmPassword ? "text" : "password"}

                    autoComplete="new-password"

                    placeholder={t(

                      "employee.confirmPasswordPlaceholder",

                      "Repeat your password",

                    )}

                    value={form.confirm_password}

                    onChange={onChange}

                    className={

                      fieldErrors.confirm_password

                        ? "oss-step1-input-error"

                        : ""

                    }

                  />

                  <button

                    type="button"

                    className="oss-step1-password-toggle"

                    onClick={() =>

                      setShowConfirmPassword((previous) => !previous)

                    }

                    aria-label={

                      showConfirmPassword ? "Hide password" : "Show password"

                    }

                  >

                    {showConfirmPassword ? (

                      <EyeOff size={18} />

                    ) : (

                      <Eye size={18} />

                    )}

                  </button>

                </div>

                {fieldErrors.confirm_password ? (

                  <span>{fieldErrors.confirm_password}</span>

                ) : null}

              </div>

              <div className="oss-step1-field oss-step1-field--full">

                <PasswordStrengthChecklist password={form.password} />

              </div>

            </>

          ) : null}

          {/* Section */}

          {isNewOss ? (

            <div className="oss-step1-field oss-step1-field--full">

              <label>

                {t("fields.regionsCovered", "Regions covered")}

                <span className="oss-required">*</span>

              </label>

              <PostalCodeCoverageSelector

                countryCode={form.country_code}

                value={form.coverage_postal_codes}

                onChange={(postalCodes) => {

                  setForm((previous) => ({

                    ...previous,

                    coverage_postal_codes: postalCodes,

                  }));

                  setFieldErrors((previous) => ({

                    ...previous,

                    coverage_postal_codes: "",

                  }));

                }}

                error={fieldErrors.coverage_postal_codes}

              />

            </div>

          ) : null}

        </div>

        {/* Section */}

        {isNewOss ? (

          <>

            <div className="oss-step1-type-block">

              <label className="oss-step1-section-label">

                {t("fields.ossType")}

                <span className="oss-required">*</span>

              </label>

              <div className="oss-step1-checkbox-grid">

                <label className="oss-step1-checkbox-card">

                  <input

                    type="checkbox"

                    name="is_advisory"

                    checked={form.is_advisory}

                    onChange={onChange}

                  />

                  <span>

                    <strong>{t("ossTypes.advisory.title")}</strong>

                    <small>{t("ossTypes.advisory.description")}</small>

                  </span>

                </label>

                <label className="oss-step1-checkbox-card">

                  <input

                    type="checkbox"

                    name="is_turnkey_renovation"

                    checked={form.is_turnkey_renovation}

                    onChange={onChange}

                  />

                  <span>

                    <strong>{t("ossTypes.turnkey.title")}</strong>

                    <small>{t("ossTypes.turnkey.description")}</small>

                  </span>

                </label>

              </div>

              {fieldErrors.oss_type ? (

                <span className="oss-step1-field-error">

                  {fieldErrors.oss_type}

                </span>

              ) : null}

            </div>

            <div className="oss-step1-field oss-step1-full">

              <label>{t("fields.additionalMessage")}</label>

              <textarea

                name="message"

                rows="4"

                placeholder={t("placeholders.additionalMessage")}

                value={form.message}

                onChange={onChange}

              />

            </div>

          </>

        ) : (

          <div className="oss-step1-employee-info">

            <ShieldCheck size={20} />

            <div>

              <strong>

                {t(

                  "employee.accessTitle",

                  "Separate account, shared organisation",

                )}

              </strong>

              <p>

                {t(

                  "employee.accessText",

                  "You will have your own login, email verification and MFA while accessing the same OSS projects, requests and organisation workspace as your colleagues.",

                )}

              </p>

            </div>

          </div>

        )}

        {/* Section */}

        <div

          className={[

            "oss-terms-block",

            fieldErrors.agree_terms ? "oss-terms-block--error" : "",

          ]

            .filter(Boolean)

            .join(" ")}

        >

          <div className="oss-terms-row">

            <input

              id="oss-agree-terms"

              type="checkbox"

              name="agree_terms"

              checked={form.agree_terms}

              onChange={onChange}

            />

            <label htmlFor="oss-agree-terms" className="oss-terms-consent">

              {t("termsCheckPrefix", "I agree to the")}

            </label>

            <button

              type="button"

              className="oss-terms-link"

              onClick={() => setTermsModalOpen(true)}

            >

              {t("termsModal.link", "Terms and Conditions")}

            </button>

            <span className="oss-terms-required" aria-hidden="true">

              *

            </span>

          </div>

          {fieldErrors.agree_terms ? (

            <span className="oss-step1-field-error">

              {fieldErrors.agree_terms}

            </span>

          ) : null}

        </div>

        {/* Section */}

        {err || ok ? (

          <div className="oss-step1-message-area">

            {err ? <div className="oss-step1-message error" style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxHeight: "none", height: "auto", overflow: "visible" }}>{err}</div> : null}

            {ok ? <div className="oss-step1-message success">{ok}</div> : null}

          </div>

        ) : null}

        {/* Section */}

        <div className="oss-step1-footer">

          <div>

            {isNewOss ? (

              <p>

                {t("step1.alreadyHaveCode")}{" "}

                <Link to="/register/oss/access-code">

                  {t("step1.completeOnboarding")}

                </Link>

              </p>

            ) : (

              <p>

                {t("employee.noCompanyKey", "Do not have a company key?")}{" "}

                <button

                  type="button"

                  className="oss-step1-footer-link"

                  onClick={() =>

                    changeRegistrationMode(REGISTRATION_MODES.NEW_OSS)

                  }

                >

                  {t("employee.registerNew", "Register a new OSS")}

                </button>

              </p>

            )}

          </div>

          <button type="submit" className="oss-step1-submit" disabled={loading}>

            {loading

              ? t("buttons.submitting")

              : isNewOss

                ? t("buttons.submitRequest")

                : t("buttons.createEmployeeAccount", "Create employee account")}

          </button>

        </div>

      </form>

      <TermsConditionsModal

        open={termsModalOpen}

        onClose={() => setTermsModalOpen(false)}

      />

    </div>

  );

}
